import { useMemo, useEffect, useCallback, useContext } from 'react'
import cn from 'classnames'
import * as atoms from '../../atoms'
import Popover from 'antd/lib/popover'
import { useNavigate, Link, useLocation } from 'react-router-dom'
import cloneDeep from 'lodash/cloneDeep'
import { useAtom } from 'jotai/index'
import formatDistanceToNow from 'date-fns/formatDistanceToNow'
import Menu from 'antd/lib/menu'
import { ProjectFeaturesList, SCRIPT_TYPES } from '../../../../../constants/constants'
import { AuthContext } from '../../../../../context/firebase-context'
import { useTheme } from '../../../ThemeProvider/ThemeProvider'
import { parseGenre } from '../../../../../common/utils'
import { removeHtmlTags } from '../../../../../common/editor-utils'
import showPaywall from '../../../../../services/show-paywall'
import styles from './ProjectEntities.module.scss'
import './overrideMenuStyles.scss'
import { AVAILABLE_FOR_SHARED_PROJECT } from '../../../../../common/constants'
import { hasProFeatureEntitlement, shouldUseSharedProjectRoutes } from '../../../../../common/workspace-entitlements'
import { canAccessVideoEditor } from '../../../../../common/video-editor-access'
import useWorkspaceSwitcher from '../../../../../hooks/useWorkspaceSwitcher'

const ScriptBody = ({ item }) => {
  const date = item.updated_at || item.updatedAt
  return (
    <div className={styles.bodyContainer}>
      {date && (
        <div className={styles.bodyItem}>
          <div className={styles.itemTitle}>Last Edited</div>
          <div className={styles.itemValue}>
            {formatDistanceToNow(new Date(date), { addSuffix: true })}
          </div>
        </div>
      )}
      <div className={styles.bodyItem}>
        <div className={styles.itemTitle}>Genre</div>
        <div className={styles.itemValue}>{parseGenre(item.genre).replace('Video Short,', '')}</div>
      </div>
      {item.type === 'episodicPilot' && (
        <div className={styles.bodyItem}>
          <div className={styles.itemTitle}>Format</div>
          <div className={styles.itemValue}>{item?.format ? item.format : 'N/A'}</div>
        </div>
      )}
      <div className={styles.bodyItem}>
        <div className={styles.itemTitle}>Language</div>
        <div className={styles.itemValue}>{item?.language?.name || 'English'}</div>
      </div>
      <div className={styles.bodyItem}>
        <div className={styles.itemTitle}>Synopsis</div>
        <div className={styles.itemValue}>{item.synopsis}</div>
      </div>

    </div>
  )
}

const ProjectEntities = () => {
  const { themeMode } = useTheme()
  const navigate = useNavigate()
  const location = useLocation()
  const user = useContext(AuthContext)
  const { activeWorkspace } = useWorkspaceSwitcher()
  const [selectedProject] = useAtom(atoms.selectedProject)
  const [projectScripts] = useAtom(atoms.scriptsByProject)
  const [, setProjectScripts] = useAtom(atoms.setProjectScripts)

  const menuItems = useMemo(() => {
    const scripts = projectScripts[selectedProject.uuid] || []
    let menuItems = cloneDeep(ProjectFeaturesList).reduce((acc, val) => {
      if (val.children) {
        acc.push(...val.children.map(c => {
          c.label = c.title
          c.key = `/project/${selectedProject.uuid}?s=${c.id}`
          return c
        }))
      } else {
        val.label = val.title
        val.key = `/project/${selectedProject.uuid}?s=${val.id}`
        acc.push(val)
      }
      return acc
    }, [])

    if (scripts.length) {
      const shared = shouldUseSharedProjectRoutes({ pathname: location.pathname, project: selectedProject })
      menuItems = menuItems.map(val => {
        val.key = val.id
        val.children = scripts.map((s) => {
          const scriptType = SCRIPT_TYPES[s?.data?.type]
          let url = val.id === 'storyboard'
            ? `/editor/storyboard/${selectedProject.uuid}/${s.uuid}`
            : `/${val.id}/${selectedProject.uuid}/${s.uuid}`
          if (shared && [...AVAILABLE_FOR_SHARED_PROJECT, 'editor'].includes(val.id)) {
            url = `/shared_project/${selectedProject.uuid}/${val.id}/${s.uuid}`
          }
          return {
            id: s.uuid,
            icon: scriptType?.icon,
            key: url,
            label: (
              <Popover placement='right' content={<ScriptBody item={{ ...s, ...s.data }} />}>
                {(val.id === 'video-editor' && !canAccessVideoEditor({ activeWorkspace, project: selectedProject, user }))
                  ? (
                    <div onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      showPaywall('creator', { paywallType: 'feature', featureName: 'aiVideo', trigger: 'left_nav', targetPath: url })
                    }}
                    >
                      <p className={styles.title}>{removeHtmlTags(s.data.title)}</p>
                    </div>
                    )
                  : (val.id === 'budgeting-forecast')
                      ? (
                        <div onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          let url = `/budgeting-forecast/${selectedProject.uuid}/${s.uuid}`
                          if (shared) {
                            url = `/shared_project/${selectedProject.uuid}/budgeting-forecast/${s.uuid}`
                          }
                          if (!hasProFeatureEntitlement({ activeWorkspace, project: selectedProject, user })) {
                            return showPaywall('pro', { paywallType: 'feature', featureName: 'budgeting', trigger: 'left_nav', targetPath: url })
                          }
                          navigate(url)
                          return navigate(0)
                        }}
                        >
                          <p className={styles.title}>{removeHtmlTags(s.data.title)}</p>
                        </div>
                        )
                      : (
                        <Link to={url}>
                          <p className={styles.title}>{removeHtmlTags(s.data.title)}</p>
                        </Link>
                        )}

              </Popover>
            )
          }
        })
        return val
      })
    }
    let projectType
    try {
      const projectTypeData = JSON.parse(selectedProject.type)
      projectType = SCRIPT_TYPES[projectTypeData.value]
    } catch (e) {
    }
    menuItems.unshift({
      id: selectedProject.uuid,
      icon: projectType?.icon,
      key: `/project/${selectedProject.uuid}`,
      label: (
        <Link to={`/project/${selectedProject.uuid}`}>
          <p className={styles.title}>Go To "{selectedProject.title}"</p>
        </Link>
      )
    })
    return menuItems
  }, [activeWorkspace, location.pathname, navigate, projectScripts, selectedProject, user])

  const handleMenuItemClick = useCallback((event) => {
    navigate(event.key)
    navigate(0)
  }, [navigate])

  useEffect(() => {
    setProjectScripts({ project: selectedProject })
  }, [])

  return selectedProject && (
    <div className={cn(styles.container, styles[themeMode])}>
      <Menu
        inlineIndent={8}
        className={cn('root-project-sub-navigation', themeMode)}
        mode='inline'
        onSelect={handleMenuItemClick}
        items={menuItems}
      />
    </div>
  )
}

export default ProjectEntities
